import { useState } from "react";
import { HallSummary, ManagementApi } from "./api";

type Preview = { inventory_sha256: string; blockers: string[]; manifest: Record<string, unknown> };

/** Owner maintenance only, never part of routine POS enrollment. */
export function DemoRetirement({ hall, api, onChanged }: { hall: HallSummary; api: ManagementApi; onChanged: () => void }): React.JSX.Element | null {
  const [preview, setPreview] = useState<Preview>();
  const [receipts, setReceipts] = useState<Record<string, unknown>[]>();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false), [status, setStatus] = useState("");
  const [pending, setPending] = useState<Record<string, unknown>>();
  if (hall.hall_id !== "hall-demo-v17" || hall.status !== "active") return null;
  const root = `/v1/halls/${hall.hall_id}`;
  async function run(action: () => Promise<void>) {
    setBusy(true); setStatus("");
    try { await action(); } catch (failure) { setStatus(failure instanceof Error ? failure.message : "Retirement not confirmed."); }
    finally { setBusy(false); }
  }
  return <details><summary>Demo retirement — owner maintenance</summary>
    <p>Only this old test company. First verify protected local backups and permanent retirement on both devices. Original signed history is retained; company removal remains a separate recoverable action.</p>
    <button disabled={busy || Boolean(pending)} onClick={() => void run(async () => { setPreview(await api.get<Preview>(`${root}/retirement-preview`)); setReceipts(undefined); })}>Check retirement prerequisites</button>
    {preview && <><pre aria-label="Demo retirement inventory">{JSON.stringify(preview, null, 2)}</pre>
      <fieldset disabled={busy || Boolean(pending)}>
        <label>Signed local retirement receipts<input type="file" accept="application/json,.json" onChange={event => {
          const file = event.target.files?.[0]; event.target.value = ""; setReceipts(undefined);
          void run(async () => {
            if (!file || file.size > 16_384) throw new Error("Two signed local receipts are required.");
            const value: unknown = JSON.parse(await file.text());
            if (!Array.isArray(value) || value.length !== 2 || value.some(item => !item || typeof item !== "object"
              || item.hall_id !== hall.hall_id || item.cloud_inventory_sha256 !== preview.inventory_sha256
              || item.schema_version !== "fivecrown-local-retirement-v1") || value[0].reset_id !== value[1].reset_id) throw new Error("Receipt scope does not match the inspected demo.");
            setReceipts(value);
          });
        }} /></label>
        <label>Confirm demo company name<input autoComplete="off" value={confirm} onChange={event => setConfirm(event.target.value)} /></label>
      </fieldset>
      <button className="danger-button" disabled={busy || (!pending && (!receipts || confirm !== hall.display_name || preview.blockers.length > 0))}
        onClick={() => void run(async () => {
          const body = pending ?? { hall_id: hall.hall_id, reset_id: receipts![0].reset_id,
            confirm_name: confirm, inventory_sha256: preview.inventory_sha256, receipts };
          setPending(body);
          await api.mutate(`${root}/retire-demo-devices`, "POST", body, String(body.reset_id));
          setStatus("Demo devices retired and access suspended. Original history retained. Remove the company using its existing Delete company action.");
          onChanged();
        })}>{pending ? "Retry same demo retirement" : "Retire verified demo devices"}</button>
    </>}
    <p role="status">{status}</p>
  </details>;
}
